import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import apiClient, { formatApiError } from "@/api/client";
import { ChevronLeft, Sparkles, Loader2, ChevronRight, ArrowRight, ListTree, X, Plus } from "lucide-react";
import { toast } from "sonner";
import CustomerSelect from "@/components/CustomerSelect";

const TONES = [
  "Profesyonel", "Samimi & Sıcak", "Lüks & Prestijli", "Aciliyet Vurgulu",
  "Eğitici & Bilgilendirici", "Eğlenceli & Yaratıcı", "Güven Verici", "Promosyon Odaklı",
];

export default function AdCopyNewPage() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [customers, setCustomers] = useState([]);
  const [lists, setLists] = useState([]);
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const [customerId, setCustomerId] = useState("");
  const [keywordSource, setKeywordSource] = useState("manual"); // manual | list
  const [listId, setListId] = useState(params.get("list_id") || "");
  const [manualKw, setManualKw] = useState("");
  const [manualNeg, setManualNeg] = useState("");
  const [businessInfo, setBusinessInfo] = useState("");
  const [tone, setTone] = useState("Profesyonel");
  const [customTone, setCustomTone] = useState("");
  const [extra, setExtra] = useState("");

  useEffect(() => {
    apiClient.get("/customers").then(r => setCustomers(r.data || []));
    apiClient.get("/keyword-lists").then(r => setLists(r.data || []));
  }, []);

  // If list_id param is present, prefill source = list
  useEffect(() => { if (params.get("list_id")) setKeywordSource("list"); }, []); // eslint-disable-line

  const selectedList = useMemo(() => lists.find(l => l.id === listId), [lists, listId]);

  // Auto-prefill business info from customer record
  const selectedCustomer = customers.find(c => c.id === customerId);
  useEffect(() => {
    if (selectedCustomer && !businessInfo) {
      const parts = [
        selectedCustomer.company && `Şirket: ${selectedCustomer.company}`,
        selectedCustomer.industry && `Sektör: ${selectedCustomer.industry}`,
        selectedCustomer.notes && selectedCustomer.notes.slice(0, 200),
      ].filter(Boolean);
      if (parts.length) setBusinessInfo(parts.join("\n"));
    }
  }, [selectedCustomer]); // eslint-disable-line

  // If list selected and has customer, prefill
  useEffect(() => {
    if (selectedList?.customer_id && !customerId) setCustomerId(selectedList.customer_id);
  }, [selectedList]); // eslint-disable-line

  const keywordsParsed = keywordSource === "manual"
    ? manualKw.split(/[\n,]/).map(s=>s.trim()).filter(Boolean)
    : (selectedList?.positives || []).map(p=>p.keyword);
  const negativesParsed = keywordSource === "manual"
    ? manualNeg.split(/[\n,]/).map(s=>s.trim()).filter(Boolean)
    : (selectedList?.negatives || []).map(p=>p.keyword);

  const start = async () => {
    if (!keywordsParsed.length) return toast.error("En az bir anahtar kelime gerekli");
    if (!businessInfo.trim()) return toast.error("İş/marka bilgisi gerekli");
    setSubmitting(true);
    try {
      const { data } = await apiClient.post("/ai-tools/ad-copy/jobs", {
        customer_id: customerId || null,
        keyword_list_id: keywordSource === "list" ? (listId || null) : null,
        keywords: keywordsParsed,
        negative_keywords: negativesParsed,
        business_info: businessInfo,
        tone: customTone.trim() || tone,
        extra_instructions: extra,
      });
      toast.success("Üretim başlatıldı");
      nav(`/ai-araclar/reklam-metni/${data.job_id}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  const STEPS = ["Anahtar Kelimeler", "Marka & Ton", "Başlat"];

  return (
    <div className="space-y-5" data-testid="ad-copy-new">
      <div className="flex items-center gap-2 text-xs text-[#64748B]">
        <Link to="/ai-araclar/reklam-metni" className="hover:text-[#4F46E5] flex items-center gap-1"><ChevronLeft size={14}/>Reklam Metni Üretici</Link>
        <span>/</span><span className="font-medium text-[#0F172A]">Yeni</span>
      </div>

      <div>
        <h1 className="text-2xl font-bold">Yeni Reklam Metni</h1>
        <p className="text-sm text-[#64748B] mt-1">Anahtar kelimeleri, marka bilgisini ve tonu belirle — AI 15 başlık ve 4 açıklama üretsin.</p>
      </div>

      <div className="ky-card p-4">
        <div className="flex items-center gap-3 overflow-x-auto">
          {STEPS.map((label, i) => (
            <React.Fragment key={label}>
              <div className={`flex items-center gap-2 ${i===step?"text-[#4F46E5]":i<step?"text-[#16A34A]":"text-[#94A3B8]"}`}>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-semibold ${i===step?"bg-[#4F46E5] text-white":i<step?"bg-[#16A34A] text-white":"bg-[#F4F4F5] text-[#64748B]"}`}>{i+1}</div>
                <span className="text-xs font-medium whitespace-nowrap">{label}</span>
              </div>
              {i<STEPS.length-1 && <div className={`h-px flex-1 min-w-[20px] ${i<step?"bg-[#16A34A]":"bg-[#ECECEF]"}`}/>}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Step 0: Keywords */}
      {step === 0 && (
        <div className="ky-card p-6 space-y-4">
          <h3 className="font-semibold text-base">Anahtar Kelime Kaynağı</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            <button onClick={()=>setKeywordSource("manual")} className={`text-left p-4 rounded-lg border-2 ${keywordSource==="manual"?"border-[#4F46E5] bg-[#EEF2FF]":"border-[#ECECEF]"}`} data-testid="src-manual">
              <div className="font-semibold text-sm">Manuel Gir</div>
              <p className="text-[11px] text-[#64748B]">Kelimeleri tek tek veya virgülle/satır sonu ile yapıştır.</p>
            </button>
            <button onClick={()=>setKeywordSource("list")} className={`text-left p-4 rounded-lg border-2 ${keywordSource==="list"?"border-[#4F46E5] bg-[#EEF2FF]":"border-[#ECECEF]"}`} data-testid="src-list">
              <div className="font-semibold text-sm flex items-center gap-1.5"><ListTree size={13}/>Kayıtlı Listeden</div>
              <p className="text-[11px] text-[#64748B]">Önceden kaydettiğin anahtar kelime listesini kullan.</p>
            </button>
          </div>

          {keywordSource === "manual" && (
            <>
              <div>
                <label className="block text-xs font-medium text-[#64748B] mb-1.5">Pozitif Anahtar Kelimeler *</label>
                <textarea value={manualKw} onChange={e=>setManualKw(e.target.value)} rows={5} placeholder={"implant fiyatları\nortodonti istanbul\n..."} className="ky-textarea text-sm" data-testid="manual-kws"/>
                <div className="text-[10px] text-[#94A3B8] mt-1">{keywordsParsed.length} kelime algılandı</div>
              </div>
              <div>
                <label className="block text-xs font-medium text-[#64748B] mb-1.5">Negatif Kelimeler (opsiyonel)</label>
                <textarea value={manualNeg} onChange={e=>setManualNeg(e.target.value)} rows={3} placeholder="ücretsiz, bedava, ikinci el..." className="ky-textarea text-sm"/>
              </div>
            </>
          )}

          {keywordSource === "list" && (
            <>
              <div>
                <label className="block text-xs font-medium text-[#64748B] mb-1.5">Kayıtlı Liste *</label>
                <select value={listId} onChange={e=>setListId(e.target.value)} className="ky-input" data-testid="list-select">
                  <option value="">— liste seç —</option>
                  {lists.map(l => <option key={l.id} value={l.id}>{l.name} — {(l.positives||[]).length}+ kelime</option>)}
                </select>
              </div>
              {selectedList && (
                <div className="bg-[#FAFAFB] border border-[#ECECEF] rounded-lg p-3 text-xs">
                  <div className="font-semibold text-[#0F172A] mb-1">{selectedList.name}</div>
                  <div className="text-[#64748B]">
                    {keywordsParsed.length} pozitif · {negativesParsed.length} negatif kelime kullanılacak
                  </div>
                </div>
              )}
            </>
          )}

          <div>
            <label className="block text-xs font-medium text-[#64748B] mb-1.5">Müşteri (opsiyonel — sonradan kaydederken kullanılır)</label>
            <CustomerSelect customers={customers} value={customerId || null} onChange={(v) => setCustomerId(v || "")} placeholder="— müşteri yok —" testid="customer-select"/>
          </div>

          <div className="flex justify-end pt-2 border-t border-[#ECECEF]">
            <button onClick={()=>setStep(1)} disabled={!keywordsParsed.length} className="ky-btn ky-btn-accent" data-testid="next-1">Devam <ArrowRight size={14}/></button>
          </div>
        </div>
      )}

      {/* Step 1: Brand & Tone */}
      {step === 1 && (
        <div className="ky-card p-6 space-y-4">
          <h3 className="font-semibold text-base">Marka, Ton ve Ek Talimat</h3>
          <div>
            <label className="block text-xs font-medium text-[#64748B] mb-1.5">İş / Marka Bilgisi *</label>
            <textarea value={businessInfo} onChange={e=>setBusinessInfo(e.target.value)} rows={4} placeholder="Marka, sunulan hizmetler, USP, hedef kitle, lokasyon, ödeme avantajları…" className="ky-textarea text-sm" data-testid="business-info"/>
          </div>

          <div>
            <label className="block text-xs font-medium text-[#64748B] mb-1.5">Ton</label>
            <div className="flex gap-1.5 flex-wrap mb-2">
              {TONES.map(t => (
                <button key={t} onClick={()=>{setTone(t); setCustomTone("");}} className={`px-3 py-1.5 rounded-md text-xs font-medium border-2 ${tone===t && !customTone?"border-[#4F46E5] bg-[#EEF2FF] text-[#4F46E5]":"border-[#ECECEF] text-[#64748B]"}`} data-testid={`tone-${t}`}>{t}</button>
              ))}
            </div>
            <input value={customTone} onChange={e=>setCustomTone(e.target.value)} placeholder="…veya kendi tonunu yaz (örn: Mizahi & X jenerasyonu)" className="ky-input text-sm"/>
          </div>

          <div>
            <label className="block text-xs font-medium text-[#64748B] mb-1.5">Ekstra Talimat (opsiyonel)</label>
            <textarea value={extra} onChange={e=>setExtra(e.target.value)} rows={3} placeholder="Örn: Fiyat kesinlikle geçmesin. Telefon numarası ekle: 0212-xxx. Beyaz kapital yazım stili kullan." className="ky-textarea text-sm" data-testid="extra"/>
          </div>

          <div className="flex justify-between pt-2 border-t border-[#ECECEF]">
            <button onClick={()=>setStep(0)} className="ky-btn ky-btn-outline">← Geri</button>
            <button onClick={()=>setStep(2)} disabled={!businessInfo.trim()} className="ky-btn ky-btn-accent" data-testid="next-2">Özet <ArrowRight size={14}/></button>
          </div>
        </div>
      )}

      {/* Step 2: Summary */}
      {step === 2 && (
        <div className="ky-card p-6 space-y-4">
          <div className="flex items-center gap-2"><Sparkles size={18} className="text-[#D97706]"/><h3 className="font-semibold text-base">Hazır</h3></div>
          <div className="bg-[#FAFAFB] border border-[#ECECEF] rounded-lg p-4 text-xs space-y-2">
            <Row label="Anahtar Kelimeler" value={`${keywordsParsed.length} pozitif · ${negativesParsed.length} negatif`}/>
            <Row label="Müşteri" value={selectedCustomer?.name || "(seçilmedi)"}/>
            <Row label="Ton" value={customTone || tone}/>
            <Row label="Üretilecek" value="15 başlık · 4 açıklama"/>
          </div>
          <div className="flex justify-between pt-2 border-t border-[#ECECEF]">
            <button onClick={()=>setStep(1)} className="ky-btn ky-btn-outline">← Geri</button>
            <button onClick={start} disabled={submitting} className="ky-btn ky-btn-accent" data-testid="start-ad-copy">
              {submitting ? <><Loader2 size={14} className="animate-spin"/>Başlatılıyor…</> : <><Sparkles size={14}/>Üretimi Başlat</>}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-3"><span className="text-[#94A3B8]">{label}</span><span className="font-medium text-[#0F172A] text-right">{value}</span></div>
);
